import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2,
  Plus,
  Trash2,
  Save,
  Upload,
  X,
  Pencil,
} from 'lucide-react';
import { client } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith('/api/') ? `${API}${u}` : u);

const TABS = [
  { id: 'products', label: 'Produk' },
  { id: 'categories', label: 'Kategori' },
  { id: 'subcategories', label: 'Subkategori' },
];

export default function AdminCatalog() {
  const [tab, setTab] = useState('products');
  return (
    <div data-testid="admin-catalog-page">
      <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
        KATALOG PRODUK
      </p>
      <h1 className="font-display text-4xl font-bold text-neutral-900 mb-2">
        Katalog Produk
      </h1>
      <p className="text-neutral-600 mb-6">
        Kelola kategori, subkategori, dan produk beserta fotonya.
      </p>

      <div className="flex gap-2 mb-6 border-b border-neutral-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`catalog-tab-${t.id}`}
            className={`px-4 py-2 -mb-px text-sm font-semibold border-b-2 transition-colors ${
              tab === t.id
                ? 'border-px-red text-px-red'
                : 'border-transparent text-neutral-500 hover:text-neutral-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'products' && <ProductsAdmin />}
      {tab === 'categories' && <CategoriesAdmin />}
      {tab === 'subcategories' && <SubcategoriesAdmin />}
    </div>
  );
}

// -----------------------------------------------------------------
// Products
// -----------------------------------------------------------------
function ProductsAdmin() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [p, c, s] = await Promise.all([
        client.get('/api/admin/products'),
        client.get('/api/admin/categories'),
        client.get('/api/admin/subcategories'),
      ]);
      setItems(p.data);
      setCats(c.data);
      setSubs(s.data);
    } catch (err) {
      toast.error('Gagal memuat data');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        p.product_id.toLowerCase().includes(q)
    );
  }, [items, filter]);

  const startNew = () => {
    setEditing({
      product_id: '',
      sku: '',
      name: '',
      category: cats[0]?.slug || '',
      subcategory: '',
      series: '',
      capacity: '',
      price: null,
      rating: 4.7,
      badge: '',
      short: '',
      image: '',
      specs: {},
      features: [],
      order: 0,
      active: true,
      _isNew: true,
    });
  };

  const remove = async (pid) => {
    if (!window.confirm(`Hapus produk ${pid}?`)) return;
    try {
      await client.delete(`/api/admin/products/${pid}`);
      setItems((s) => s.filter((x) => x.product_id !== pid));
      toast.success('Produk dihapus');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Cari nama, SKU, atau ID..."
          className="flex-1 max-w-xs bg-neutral-50 border border-neutral-200 focus:border-neutral-900 outline-none rounded-full px-4 py-2 text-sm"
          data-testid="products-filter"
        />
        <span className="text-xs text-neutral-500">
          {filtered.length} / {items.length} produk
        </span>
        <button
          onClick={startNew}
          data-testid="add-product-btn"
          className="ml-auto inline-flex items-center gap-2 bg-neutral-900 hover:bg-px-red text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors"
        >
          <Plus className="w-4 h-4" /> Tambah Produk
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        {loading ? (
          <p className="p-8 text-sm text-neutral-500">Memuat...</p>
        ) : filtered.length === 0 ? (
          <p className="p-8 text-sm text-neutral-500 text-center">
            Tidak ada produk.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200 text-left text-xs uppercase text-neutral-600">
              <tr>
                <th className="px-4 py-3">Foto</th>
                <th className="px-4 py-3">Produk</th>
                <th className="px-4 py-3">Kategori</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.product_id}
                  className="border-b border-neutral-100 last:border-0"
                >
                  <td className="px-4 py-2">
                    <div className="w-14 h-14 rounded-lg bg-neutral-100 overflow-hidden flex items-center justify-center">
                      {p.image && (
                        <img
                          src={absUrl(p.image)}
                          alt=""
                          className="w-full h-full object-contain p-1"
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <p className="font-semibold text-neutral-900">{p.name}</p>
                    <p className="text-xs text-neutral-500">
                      {p.sku} · {p.capacity}
                    </p>
                  </td>
                  <td className="px-4 py-2 text-xs text-neutral-600">
                    {p.category}
                    {p.subcategory && ` / ${p.subcategory}`}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                        p.active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-neutral-200 text-neutral-600'
                      }`}
                    >
                      {p.active ? 'AKTIF' : 'NONAKTIF'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right space-x-1">
                    <button
                      onClick={() => setEditing({ ...p })}
                      data-testid={`edit-product-${p.product_id}`}
                      className="p-2 text-neutral-500 hover:text-neutral-900"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => remove(p.product_id)}
                      className="p-2 text-neutral-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <ProductModal
          product={editing}
          cats={cats}
          subs={subs}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function ProductModal({ product, cats, subs, onClose, onSaved }) {
  const [form, setForm] = useState({
    ...product,
    images:
      product.images && product.images.length > 0
        ? [...product.images]
        : product.image
        ? [product.image]
        : [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const images = form.images || [];

  const availableSubs = subs.filter((s) => s.parent_slug === form.category);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...form,
        image: images[0] || '',
        images,
        price: form.price === '' || form.price == null ? null : Number(form.price),
        badge: form.badge?.trim() || null,
        subcategory: form.subcategory || null,
        specs: form.specs || {},
        features: form.features || [],
      };
      delete body._isNew;
      if (product._isNew) {
        await client.post('/api/admin/products', body);
      } else {
        await client.put(`/api/admin/products/${product.product_id}`, body);
      }
      toast.success('Produk tersimpan');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('folder', 'products');
        const { data } = await client.post('/api/admin/upload-image', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        urls.push(data.url);
      }
      setF('images', [...(form.images || []), ...urls]);
      toast.success(`${urls.length} foto terunggah`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload gagal');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeImage = (i) =>
    setF('images', (form.images || []).filter((_, x) => x !== i));

  const makeMain = (i) => {
    const arr = [...(form.images || [])];
    const [img] = arr.splice(i, 1);
    arr.unshift(img);
    setF('images', arr);
  };

  // specs editor: array of {k, v}
  const specEntries = Object.entries(form.specs || {});

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <form
        onSubmit={save}
        className="bg-white rounded-2xl w-full max-w-3xl my-8 overflow-hidden"
        data-testid="product-modal"
      >
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
          <h2 className="font-display text-xl font-bold">
            {product._isNew ? 'Tambah Produk' : `Edit ${product.sku}`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-900"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="grid md:grid-cols-[180px_1fr] gap-5">
            <div>
              <div className="aspect-square rounded-xl bg-neutral-100 border border-neutral-200 overflow-hidden flex items-center justify-center">
                {images[0] ? (
                  <img
                    src={absUrl(images[0])}
                    alt=""
                    className="w-full h-full object-contain p-2"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-neutral-400">
                    Belum ada foto
                  </div>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => upload(e.target.files)}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="upload-product-image"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-neutral-300 hover:border-neutral-900 rounded-full py-2 text-xs font-semibold disabled:opacity-60"
              >
                {uploading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Upload className="w-3.5 h-3.5" />
                )}
                Tambah Foto
              </button>
              {images.length > 0 && (
                <div className="mt-3 space-y-1.5" data-testid="product-images-list">
                  {images.map((url, i) => (
                    <div
                      key={`${url}-${i}`}
                      data-testid={`product-image-item-${i}`}
                      className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-lg p-1.5"
                    >
                      <div className="w-9 h-9 rounded-md bg-white overflow-hidden flex-shrink-0 flex items-center justify-center">
                        <img
                          src={absUrl(url)}
                          alt=""
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <span className="text-[10px] text-neutral-500 flex-1 truncate">
                        Foto {i + 1}
                      </span>
                      {i === 0 ? (
                        <span className="text-[9px] font-bold text-px-red bg-red-50 px-1.5 py-0.5 rounded-full">
                          UTAMA
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => makeMain(i)}
                          data-testid={`make-main-${i}`}
                          className="text-[9px] font-semibold text-neutral-500 hover:text-neutral-900 px-1"
                        >
                          Jadikan utama
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        data-testid={`remove-image-${i}`}
                        className="text-neutral-400 hover:text-red-600 p-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-neutral-500 mt-2">
                {images.length} foto · foto pertama = foto utama
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Product ID" required>
                <input
                  className={ic}
                  value={form.product_id}
                  disabled={!product._isNew}
                  onChange={(e) => setF('product_id', e.target.value)}
                  data-testid="product-id-input"
                />
              </Field>
              <Field label="SKU">
                <input
                  className={ic}
                  value={form.sku}
                  onChange={(e) => setF('sku', e.target.value)}
                />
              </Field>
              <Field label="Nama Produk" full>
                <input
                  className={ic}
                  value={form.name}
                  onChange={(e) => setF('name', e.target.value)}
                  data-testid="product-name-input"
                />
              </Field>
              <Field label="Kategori">
                <select
                  className={ic}
                  value={form.category}
                  onChange={(e) => {
                    setF('category', e.target.value);
                    setF('subcategory', '');
                  }}
                >
                  {cats.map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Subkategori">
                <select
                  className={ic}
                  value={form.subcategory || ''}
                  onChange={(e) => setF('subcategory', e.target.value)}
                >
                  <option value="">— Tidak ada —</option>
                  {availableSubs.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.title}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Series">
                <input
                  className={ic}
                  value={form.series}
                  onChange={(e) => setF('series', e.target.value)}
                />
              </Field>
              <Field label="Kapasitas">
                <input
                  className={ic}
                  value={form.capacity}
                  onChange={(e) => setF('capacity', e.target.value)}
                />
              </Field>
              <Field label="Badge (opsional)">
                <input
                  className={ic}
                  placeholder="POPULAR / NEW / BEST SELLER"
                  value={form.badge || ''}
                  onChange={(e) => setF('badge', e.target.value)}
                />
              </Field>
              <Field label="Rating">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="5"
                  className={ic}
                  value={form.rating}
                  onChange={(e) => setF('rating', Number(e.target.value))}
                />
              </Field>
              <Field label="Harga (kosong = Minta Penawaran)" full>
                <input
                  type="number"
                  className={ic}
                  value={form.price ?? ''}
                  onChange={(e) => setF('price', e.target.value)}
                />
              </Field>
            </div>
          </div>

          <Field label="Deskripsi Singkat" full>
            <textarea
              rows={2}
              className={`${ic} resize-none`}
              value={form.short}
              onChange={(e) => setF('short', e.target.value)}
            />
          </Field>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] uppercase tracking-wider text-neutral-600 font-semibold">
                Spesifikasi (key → value)
              </label>
              <button
                type="button"
                onClick={() =>
                  setF('specs', { ...(form.specs || {}), '': '' })
                }
                className="text-xs font-semibold text-px-red inline-flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Tambah
              </button>
            </div>
            <div className="space-y-2">
              {specEntries.map(([k, v], i) => (
                <div key={i} className="grid grid-cols-[1fr_1.5fr_auto] gap-2">
                  <input
                    placeholder="Nama spec (mis. Kapasitas)"
                    defaultValue={k}
                    onBlur={(e) => {
                      const newK = e.target.value.trim();
                      if (newK === k) return;
                      const s = { ...form.specs };
                      delete s[k];
                      if (newK) s[newK] = v;
                      setF('specs', s);
                    }}
                    className={ic}
                  />
                  <input
                    placeholder="Nilai"
                    value={v}
                    onChange={(e) =>
                      setF('specs', { ...form.specs, [k]: e.target.value })
                    }
                    className={ic}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const s = { ...form.specs };
                      delete s[k];
                      setF('specs', s);
                    }}
                    className="text-neutral-400 hover:text-red-600 px-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] uppercase tracking-wider text-neutral-600 font-semibold">
                Fitur Utama
              </label>
              <button
                type="button"
                onClick={() => setF('features', [...(form.features || []), ''])}
                className="text-xs font-semibold text-px-red inline-flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Tambah
              </button>
            </div>
            <div className="space-y-2">
              {(form.features || []).map((f, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    className={ic}
                    value={f}
                    onChange={(e) => {
                      const arr = [...form.features];
                      arr[i] = e.target.value;
                      setF('features', arr);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setF('features', form.features.filter((_, x) => x !== i))
                    }
                    className="text-neutral-400 hover:text-red-600 px-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setF('active', e.target.checked)}
            />
            Aktif (tampil di website)
          </label>
        </div>

        <div className="border-t border-neutral-200 px-6 py-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="border border-neutral-300 rounded-full text-sm font-semibold px-5 py-2"
          >
            Batal
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="save-product-btn"
            className="inline-flex items-center gap-2 bg-px-red hover:bg-red-700 disabled:opacity-60 text-white font-semibold px-6 py-2 rounded-full transition-colors"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Simpan
          </button>
        </div>
      </form>
    </div>
  );
}

// -----------------------------------------------------------------
// Categories
// -----------------------------------------------------------------
function CategoriesAdmin() {
  return (
    <SimpleListAdmin
      endpoint="/api/admin/categories"
      testidPrefix="category"
      keyField="slug"
      fields={[
        { key: 'slug', label: 'Slug', required: true },
        { key: 'title', label: 'Nama Kategori', required: true },
        { key: 'subtitle', label: 'Subtitle' },
        { key: 'image', label: 'URL Gambar', full: true },
        { key: 'description', label: 'Deskripsi', textarea: true, full: true },
        { key: 'order', label: 'Urutan', type: 'number' },
      ]}
    />
  );
}

// -----------------------------------------------------------------
// Subcategories
// -----------------------------------------------------------------
function SubcategoriesAdmin() {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    client.get('/api/admin/categories').then((r) => setCats(r.data));
  }, []);
  return (
    <SimpleListAdmin
      endpoint="/api/admin/subcategories"
      testidPrefix="subcategory"
      keyField="slug"
      fields={[
        { key: 'slug', label: 'Slug', required: true },
        {
          key: 'parent_slug',
          label: 'Kategori Induk',
          required: true,
          selectOptions: cats.map((c) => ({ value: c.slug, label: c.title })),
        },
        { key: 'title', label: 'Nama', required: true },
        { key: 'short', label: 'Deskripsi Singkat', full: true },
        { key: 'order', label: 'Urutan', type: 'number' },
      ]}
    />
  );
}

// -----------------------------------------------------------------
// Generic list editor (used by cats + subs)
// -----------------------------------------------------------------
function SimpleListAdmin({ endpoint, testidPrefix, keyField, fields }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [newItem, setNewItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get(endpoint);
      setItems(data);
      setDrafts(Object.fromEntries(data.map((x) => [x[keyField], { ...x }])));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [endpoint]);

  const emptyItem = () =>
    fields.reduce(
      (a, f) => ({ ...a, [f.key]: f.type === 'number' ? 0 : '' }),
      {}
    );

  const startNew = () => setNewItem(emptyItem());

  const save = async (item, isNew) => {
    try {
      if (isNew) {
        await client.post(endpoint, item);
      } else {
        await client.put(`${endpoint}/${item[keyField]}`, item);
      }
      toast.success('Tersimpan');
      setNewItem(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menyimpan');
    }
  };

  const remove = async (key) => {
    if (!window.confirm(`Hapus ${key}?`)) return;
    try {
      await client.delete(`${endpoint}/${key}`);
      toast.success('Dihapus');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  const renderFields = (item, setItem, disableKey = false) => (
    <div className="grid grid-cols-2 gap-3">
      {fields.map((f) => (
        <Field
          key={f.key}
          label={f.label + (f.required ? ' *' : '')}
          full={f.full || f.textarea}
        >
          {f.selectOptions ? (
            <select
              className={ic}
              value={item[f.key] || ''}
              onChange={(e) => setItem({ ...item, [f.key]: e.target.value })}
            >
              <option value="">— pilih —</option>
              {f.selectOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : f.textarea ? (
            <textarea
              rows={2}
              className={`${ic} resize-none`}
              value={item[f.key] || ''}
              onChange={(e) => setItem({ ...item, [f.key]: e.target.value })}
            />
          ) : (
            <input
              type={f.type || 'text'}
              className={ic}
              disabled={f.key === keyField && disableKey}
              value={item[f.key] ?? ''}
              onChange={(e) =>
                setItem({
                  ...item,
                  [f.key]:
                    f.type === 'number' ? Number(e.target.value) : e.target.value,
                })
              }
            />
          )}
        </Field>
      ))}
    </div>
  );

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button
          onClick={startNew}
          data-testid={`add-${testidPrefix}-btn`}
          className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-px-red text-white text-sm font-semibold px-5 py-2 rounded-full"
        >
          <Plus className="w-4 h-4" /> Tambah
        </button>
      </div>

      {newItem && (
        <div className="bg-white rounded-2xl border border-neutral-900 p-5 mb-4">
          <p className="text-sm font-semibold mb-3">Baru</p>
          {renderFields(newItem, setNewItem)}
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => setNewItem(null)}
              className="text-sm text-neutral-500 px-4 py-2"
            >
              Batal
            </button>
            <button
              onClick={() => save(newItem, true)}
              className="inline-flex items-center gap-2 bg-px-red text-white text-sm font-semibold px-5 py-2 rounded-full"
            >
              <Save className="w-4 h-4" /> Simpan
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="p-8 text-sm text-neutral-500">Memuat...</p>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const draft = drafts[item[keyField]] || item;
            return (
              <div
                key={item[keyField]}
                className="bg-white rounded-2xl border border-neutral-200 p-5"
                data-testid={`${testidPrefix}-row-${item[keyField]}`}
              >
                {renderFields(
                  draft,
                  (v) =>
                    setDrafts((d) => ({ ...d, [item[keyField]]: v })),
                  true
                )}
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    onClick={() => remove(item[keyField])}
                    className="text-sm text-red-600 px-3 py-2 hover:bg-red-50 rounded-full inline-flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" /> Hapus
                  </button>
                  <button
                    onClick={() => save(draft, false)}
                    className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-px-red text-white text-sm font-semibold px-5 py-2 rounded-full"
                  >
                    <Save className="w-4 h-4" /> Simpan
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ic =
  'mt-1 w-full bg-neutral-50 border border-neutral-200 focus:border-neutral-900 outline-none rounded-lg px-3 py-2 text-sm';

const Field = ({ label, children, full, required }) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="text-[11px] uppercase tracking-wider text-neutral-600 font-semibold">
      {label}
    </label>
    {children}
  </div>
);
